/**
 * Permission Guard Component
 *
 * Protects routes based on dynamic RBAC permission KEYS (the backend-driven
 * system), as opposed to RoleGuard which gates on the static JWT roles.
 * Redirects to the unauthorized page when the user lacks the permission.
 *
 * FAILS OPEN until the permission set has loaded (a slow permissions call must
 * not bounce a legitimate user) — the backend interceptor is the real
 * enforcement; this is UX. Should be used inside AuthGuard.
 */

import { Navigate } from 'react-router-dom';

import { usePermissionStore } from '@/store/slices/permissionSlice';

interface PermissionGuardProps {
  /** Children to render if authorized */
  children: React.ReactNode;
  /** Permission key(s) — ANY-of grants access */
  perm: string | string[];
  /** Path to redirect if unauthorized (default: /unauthorized) */
  fallbackPath?: string;
}

export function PermissionGuard({
  children,
  perm,
  fallbackPath = '/unauthorized',
}: PermissionGuardProps) {
  const allowed = usePermissionStore((s) => {
    if (!s.loaded) return true; // fail-open until loaded
    if (s.isOwner) return true;
    const keys = Array.isArray(perm) ? perm : [perm];
    return keys.some((k) => s.permissions.has(k));
  });

  if (!allowed) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
}
